import { Command } from 'commander';
import chalk from 'chalk';
import Table from 'cli-table3';
import fs from 'fs-extra';
import { join } from 'path';
import { execSync } from 'child_process';
import { logger } from '../lib/logger.js';
import {
  checkRalphInstallation,
  installRalphConfig,
} from '../lib/installer.js';

function isClaudeCodeInstalled(): boolean {
  try {
    execSync('claude --version', { encoding: 'utf-8', timeout: 5000, stdio: 'pipe' });
    return true;
  } catch {
    return false;
  }
}

/**
 * Get the .ralph directory for the current project
 */
function getRalphDir(): string {
  return join(process.cwd(), '.ralph');
}

/**
 * Read Ralph logs from the logs directory
 */
async function readRalphLogs(limit: number = 50): Promise<string[]> {
  const logsDir = join(getRalphDir(), 'logs');

  if (!(await fs.pathExists(logsDir))) {
    return [];
  }

  try {
    const files = await fs.readdir(logsDir);
    const logFiles = files
      .filter(f => f.endsWith('.log'))
      .sort()
      .reverse();

    if (logFiles.length === 0) {
      return [];
    }

    // Read the most recent log file
    const latestLog = join(logsDir, logFiles[0]);
    const content = await fs.readFile(latestLog, 'utf-8');
    const lines = content.split('\n');

    return lines.slice(-limit);
  } catch {
    return [];
  }
}

/**
 * List Ralph reports
 */
async function listReports(): Promise<{ name: string; date: Date; path: string }[]> {
  const reportsDir = join(getRalphDir(), 'reports');

  if (!(await fs.pathExists(reportsDir))) {
    return [];
  }

  try {
    const files = await fs.readdir(reportsDir);
    const reports: { name: string; date: Date; path: string }[] = [];

    for (const file of files) {
      if (file.endsWith('.md') || file.endsWith('.json')) {
        const filePath = join(reportsDir, file);
        const stat = await fs.stat(filePath);
        reports.push({
          name: file,
          date: stat.mtime,
          path: filePath,
        });
      }
    }

    return reports.sort((a, b) => b.date.getTime() - a.date.getTime());
  } catch {
    return [];
  }
}

// Main ralph command
export const ralphCommand = new Command('ralph')
  .description('Ralph Weegund automation loop management')
  .action(() => {
    // Default action: show help
    console.log(chalk.cyan(`
╔══════════════════════════════════════════════╗
║      ${chalk.bold('Ralph Weegund')} - SmartStack Loop          ║
╚══════════════════════════════════════════════╝
`));
    console.log('Available commands:');
    console.log(`  ${chalk.cyan('smartstack ralph start')}   - Verify prerequisites and prepare Ralph`);
    console.log(`  ${chalk.cyan('smartstack ralph status')}  - Show Ralph status and configuration`);
    console.log(`  ${chalk.cyan('smartstack ralph logs')}    - View recent Ralph logs`);
    console.log(`  ${chalk.cyan('smartstack ralph report')}  - List or view feature reports`);
    console.log(`  ${chalk.cyan('smartstack ralph init')}    - Initialize Ralph in current project`);
    console.log();
    console.log(`After running ${chalk.cyan('ralph start')}, use ${chalk.cyan('/ba-develop')} in Claude Code.`);
  });

// ralph start
ralphCommand
  .command('start')
  .description('Verify prerequisites and prepare Ralph for use')
  .action(async () => {
    logger.header('Ralph Weegund - Pre-flight Check');

    let allChecksPass = true;

    // Step 1: Check Claude Code
    logger.step(1, 4, 'Checking Claude Code CLI...');
    if (isClaudeCodeInstalled()) {
      logger.success('Claude Code CLI is installed');
    } else {
      logger.error('Claude Code CLI is NOT installed');
      if (process.platform === 'win32') {
        console.log(`  Windows:  ${chalk.cyan('irm https://claude.ai/install.ps1 | iex')} (PowerShell)`);
        console.log(`  WSL:      ${chalk.cyan('curl -fsSL https://claude.ai/install.sh | bash')}`);
      } else {
        console.log(`  Install:  ${chalk.cyan('curl -fsSL https://claude.ai/install.sh | bash')}`);
      }
      console.log(`  More info: ${chalk.cyan('https://code.claude.com/docs/en/setup')}`);
      allChecksPass = false;
    }

    // Step 2: Check .ralph directory
    logger.step(2, 4, 'Checking Ralph configuration...');
    const ralphStatus = await checkRalphInstallation();

    if (ralphStatus.installed && ralphStatus.configExists) {
      logger.success('Ralph configuration found');
      console.log(`  Config: ${chalk.cyan('.ralph/ralph.config.yaml')}`);
    } else {
      logger.warning('Ralph not configured in this project');
      console.log(`  Run: ${chalk.cyan('smartstack ralph init')} to initialize`);

      // Auto-initialize if not present
      logger.info('Auto-initializing Ralph configuration...');
      const initResult = await installRalphConfig();
      if (initResult.success) {
        logger.success('Ralph configuration created');
      } else {
        logger.error('Failed to create Ralph configuration');
        allChecksPass = false;
      }
    }

    // Step 3: Check logs directory
    logger.step(3, 4, 'Checking logs directory...');
    const logsDir = join(getRalphDir(), 'logs');
    if (await fs.pathExists(logsDir)) {
      logger.success('Logs directory ready');
    } else {
      await fs.ensureDir(logsDir);
      logger.success('Logs directory created');
    }

    // Step 4: Check reports directory
    logger.step(4, 4, 'Checking reports directory...');
    const reportsDir = join(getRalphDir(), 'reports');
    if (await fs.pathExists(reportsDir)) {
      logger.success('Reports directory ready');
    } else {
      await fs.ensureDir(reportsDir);
      logger.success('Reports directory created');
    }

    console.log();

    // Final status
    if (allChecksPass) {
      logger.box([
        'Ralph is ready!',
        '',
        'Start Ralph in Claude Code with:',
        '  /ba-develop "Your feature description"',
      ], 'success');
      process.exit(0);
    } else {
      logger.box([
        'Ralph pre-flight check failed!',
        '',
        'Please fix the issues above before using Ralph.',
        '',
        'For diagnostics: smartstack doctor',
      ], 'error');
      process.exit(1);
    }
  });

// ralph status
ralphCommand
  .command('status')
  .description('Show Ralph status and configuration')
  .action(async () => {
    logger.header('Ralph Status');

    const ralphStatus = await checkRalphInstallation();

    // Status table
    const table = new Table({
      style: { head: [], border: [] },
    });

    table.push(
      [chalk.bold('Configuration'), ralphStatus.configExists ? chalk.green('✓ Found') : chalk.red('✗ Missing')],
      [chalk.bold('Logs Directory'), ralphStatus.logsDir ? chalk.green('✓ Ready') : chalk.yellow('✗ Not created')],
      [chalk.bold('Reports Directory'), ralphStatus.reportsDir ? chalk.green('✓ Ready') : chalk.yellow('✗ Not created')],
    );

    console.log(table.toString());
    console.log();

    // Recent activity
    const reports = await listReports();
    if (reports.length > 0) {
      console.log(chalk.bold('Recent Reports:'));
      for (const report of reports.slice(0, 5)) {
        console.log(`  ${chalk.gray(report.date.toLocaleDateString())} ${report.name}`);
      }
      console.log();
    }

    // Log stats
    const logsDir = join(getRalphDir(), 'logs');
    if (await fs.pathExists(logsDir)) {
      const logFiles = (await fs.readdir(logsDir)).filter(f => f.endsWith('.log'));
      if (logFiles.length > 0) {
        console.log(chalk.bold('Logs:'));
        console.log(`  ${logFiles.length} log file(s)`);
        console.log();
      }
    }

    // Overall status
    const ready = ralphStatus.configExists;
    if (ready) {
      logger.success('Ralph is ready to use');
    } else {
      logger.warning('Ralph requires setup. Run: smartstack ralph start');
    }
  });

// ralph logs
ralphCommand
  .command('logs')
  .description('View recent Ralph logs')
  .option('-n, --lines <number>', 'Number of lines to show', '50')
  .option('-f, --follow', 'Follow log output (like tail -f)')
  .action(async (options) => {
    const lines = parseInt(options.lines, 10) || 50;

    logger.header('Ralph Logs');

    const logLines = await readRalphLogs(lines);

    if (logLines.length === 0) {
      logger.info('No logs found');
      console.log(`  Logs are stored in: ${chalk.cyan('.ralph/logs/')}`);
      return;
    }

    console.log(chalk.gray(`Showing last ${logLines.length} lines:`));
    console.log(chalk.gray('─'.repeat(60)));

    for (const line of logLines) {
      // Color-code log levels
      if (line.includes('[ERROR]') || line.includes('✗')) {
        console.log(chalk.red(line));
      } else if (line.includes('[WARN]') || line.includes('⚠')) {
        console.log(chalk.yellow(line));
      } else if (line.includes('[SUCCESS]') || line.includes('✓')) {
        console.log(chalk.green(line));
      } else {
        console.log(line);
      }
    }

    console.log(chalk.gray('─'.repeat(60)));
  });

// ralph report
ralphCommand
  .command('report')
  .description('List or view feature reports')
  .option('--last', 'Show the most recent report')
  .option('--list', 'List all reports')
  .argument('[name]', 'Report name to view')
  .action(async (name, options) => {
    const reports = await listReports();

    if (reports.length === 0) {
      logger.info('No reports found');
      console.log(`  Reports are stored in: ${chalk.cyan('.ralph/reports/')}`);
      return;
    }

    // List all reports
    if (options.list || (!name && !options.last)) {
      logger.header('Ralph Reports');

      const table = new Table({
        head: [chalk.cyan('Date'), chalk.cyan('Report')],
        style: { head: [], border: [] },
      });

      for (const report of reports) {
        table.push([
          chalk.gray(report.date.toLocaleString()),
          report.name,
        ]);
      }

      console.log(table.toString());
      console.log();
      console.log(`View a report: ${chalk.cyan('smartstack ralph report <name>')}`);
      return;
    }

    // Show specific or last report
    let reportToShow: typeof reports[0] | undefined;

    if (options.last) {
      reportToShow = reports[0];
    } else if (name) {
      reportToShow = reports.find(r => r.name.includes(name));
    }

    if (!reportToShow) {
      logger.error(`Report not found: ${name}`);
      return;
    }

    logger.header(`Report: ${reportToShow.name}`);
    console.log(chalk.gray(`Date: ${reportToShow.date.toLocaleString()}`));
    console.log(chalk.gray('─'.repeat(60)));

    const content = await fs.readFile(reportToShow.path, 'utf-8');
    console.log(content);
  });

// ralph init
ralphCommand
  .command('init')
  .description('Initialize Ralph in the current project')
  .option('-f, --force', 'Overwrite existing configuration')
  .action(async (options) => {
    logger.header('Initialize Ralph');

    const existing = await checkRalphInstallation();

    if (existing.configExists && !options.force) {
      logger.warning('Ralph is already configured in this project');
      console.log(`  Use ${chalk.cyan('--force')} to overwrite`);
      return;
    }

    const result = await installRalphConfig({ force: options.force });

    if (result.success) {
      logger.box([
        'Ralph initialized successfully!',
        '',
        `Config: ${result.configPath}`,
        `Logs: ${result.logsPath}`,
        `Reports: ${result.reportsPath}`,
        '',
        'Next: smartstack ralph start',
      ], 'success');
    } else {
      logger.error('Failed to initialize Ralph');
      for (const error of result.errors) {
        console.log(`  ${chalk.red('•')} ${error}`);
      }
    }
  });
